/**
 * Blueprint — seed in, appearance out. PROTOTYPE for docs/CHARACTERS.md.
 *
 * Designed to the agreed reference art (docs/reference — chars_roster /
 * chars_parts / chars_poses): five archetype bodies whose identity is carried
 * by colour + headgear + silhouette, assembled from a parts kit of chunky
 * blocks. "Thousands of them" is a parts-combination problem, not a modelling
 * problem — chars_parts.jpg is the kit this file draws from.
 *
 * The one rule this file exists to enforce: appearance is a PURE FUNCTION of
 * `(characterId, seedString, BLUEPRINT_VERSION)`. No Math.random, no clocks,
 * no per-client state. Every client that knows a player's id derives the same
 * blueprint, so every screen draws the same body. The RNG is the sim's own
 * (`hashSeed` + mulberry32 from @chaos/sim), not a copy.
 *
 * ── The draw-order contract ──────────────────────────────────────────────
 *
 * Axes are drawn from the RNG in a FIXED ORDER. Inserting a draw in the middle
 * reshuffles every axis after it and silently re-rolls every player's look, so:
 * new axes APPEND, removed axes keep a dead draw in place, and any breaking
 * change bumps BLUEPRINT_VERSION (which is hashed into the seed, so a v2 look
 * is a fresh universe rather than a scrambled v1).
 *
 * ── What is NOT drawn here ───────────────────────────────────────────────
 *
 * The family colour and the family tell (the bull's horns, the bear's head,
 * the vault door, the chrome dome, the satellite dish). Those ARE the roster
 * — locked per ticker, unvaryable. Variation lives strictly BELOW identity.
 */

import { hashSeed, nextFloat, nextInt, nextU32 } from '@chaos/sim';
import { CHARACTERS } from '@chaos/protocol';

export const BLUEPRINT_VERSION = 2;

/**
 * The reference roster, mapped onto the five archetype ids (mapping is a
 * proposal — see CHARACTERS.md §7; stats and ability ids do not move):
 *
 *   volt / NVDA     → BULL    green knight, horned helmet (charges: blink_strike)
 *   monolith / AAPL → VAULT   gold, safe-door torso (walled garden, 150 HP)
 *   hex / GOOGL     → BEAR    red bear head (traps and pulls: gravity_well)
 *   mirage / TSLA   → ANDROID white chrome dome (phantom / fade)
 *   talon / SPCX    → SATBOT  blue, shoulder satellite dish (Starlink barrage)
 *
 * Body colours come from `CHARACTERS[id].colorHex` in @chaos/protocol, which is
 * the ONE place a character's identity colour lives. This table used to carry
 * its own copy, which is how the game came to disagree with itself — a player
 * whose body was deep green had a cyan nameplate and a cyan lobby card.
 *
 * The protocol values are the reference palette tone-shifted off the signal
 * hues (hazard red 0xff3d3d, heal green 0x4ade80), which is why the bear is a
 * brick red rather than a pure one. What may be painted on the arena FLOOR is a
 * separate question with a separate answer — see `telegraphHex`.
 */
export const FAMILIES = {
  volt: {
    ticker: 'NVDA',
    look: 'BULL',
    trimHex: 0xd8cfae, // horn bone
    tell: 'bullHelm',
    heads: ['cube', 'rounded'],
    torsos: ['S', 'M'],
    accessories: ['none', 'shoulderPlate', 'cap'],
    face: 'visor', // helmet slit — the reference bull shows no eyes
  },
  monolith: {
    ticker: 'AAPL',
    look: 'VAULT',
    trimHex: 0x8a6a1c, // deep gold
    tell: 'vaultTorso',
    heads: ['cube', 'wedge'],
    torsos: ['L'], // the torso IS the vault; it does not shrink
    accessories: ['none', 'shoulderPlate', 'antenna'],
    face: 'visor',
  },
  hex: {
    ticker: 'GOOGL',
    look: 'BEAR',
    trimHex: 0xd9705f, // muzzle
    tell: 'bearHead',
    heads: ['rounded', 'cube'],
    torsos: ['M', 'L'],
    accessories: ['none', 'cap', 'shoulderPlate'],
    face: 'eyes',
  },
  mirage: {
    ticker: 'TSLA',
    look: 'ANDROID',
    trimHex: 0x9aa7b8, // steel
    tell: 'chromeDome',
    heads: ['rounded'], // the dome is the head
    torsos: ['S', 'M'],
    accessories: ['none', 'antenna', 'cap'],
    face: 'visor',
  },
  talon: {
    ticker: 'SPCX',
    look: 'SATBOT',
    trimHex: 0x1a4f96, // deep blue
    tell: 'satDish',
    heads: ['tall', 'cube', 'wedge'],
    torsos: ['S', 'M'],
    accessories: ['none', 'visorGlasses', 'cap'],
    face: 'eyes',
  },
};

/**
 * Accent swatches — the seed's SECONDARY colour (belts, boots, pads).
 * Prop palette + neutrals; never a hue that competes with a family primary or
 * reads as a signal. Secondary must always lose to primary at phone size.
 */
export const ACCENT_SWATCHES = [
  0x2a3040, // prop carcass — near-black navy
  0x4a556b, // prop panel — slate
  0x8794a6, // prop metal — grey-blue
  0x6d5748, // prop warm panel — leather
  0xd9e2ec, // prop top — bone white
  0x1a1e2c, // ink
];

/** The parts kit (chars_parts.jpg): limb styles are STYLES, not just lengths. */
const ARM_STYLES = ['block', 'seg', 'chunky'];
const LEG_STYLES = ['block', 'seg', 'slab'];
const PATTERNS = ['solid', 'belt', 'stripe', 'panel'];
const EYE_STYLES = ['round', 'wide', 'narrow'];
const MOUTHS = ['line', 'frown', 'open'];

function pick(rng, list) {
  return list[nextInt(rng, 0, list.length)];
}

/**
 * The pure function. `seedString` is the player's stable id —
 * `PlayerSnapshot.id` / `world_init.you.userId` — identical on every client,
 * stable across reconnects and across matches. Bots use their server-assigned
 * ids through the same path.
 */
export function blueprintFor(characterId, seedString) {
  const family = FAMILIES[characterId] ?? FAMILIES.volt;
  // Identity colour has exactly one owner, and it is the roster.
  const identityHex = (CHARACTERS[characterId] ?? CHARACTERS.volt).colorHex;
  const familyId = FAMILIES[characterId] ? characterId : 'volt';

  const rng = { rngState: hashSeed(`${familyId}:${seedString}:v${BLUEPRINT_VERSION}`) };

  // ── FIXED DRAW ORDER — append only ──────────────────────────
  const headBlock = pick(rng, family.heads); // 1
  const torsoSize = pick(rng, family.torsos); // 2
  const armStyle = pick(rng, ARM_STYLES); // 3
  const legStyle = pick(rng, LEG_STYLES); // 4
  const accessory = pick(rng, family.accessories); // 5
  const accentHex = pick(rng, ACCENT_SWATCHES); // 6
  const pattern = pick(rng, PATTERNS); // 7
  const eyeStyle = pick(rng, EYE_STYLES); // 8 (dead draw for visor faces)
  const mouth = pick(rng, MOUTHS); // 9 (dead draw for visor faces)
  const phaseOffset = nextFloat(rng); // 10 — idle desync
  const scatterSeed = nextU32(rng); // 11 — the defeat scatter (deterministic)

  return {
    version: BLUEPRINT_VERSION,
    family: familyId,
    ticker: family.ticker,
    look: family.look,
    colorHex: identityHex, // LOCKED — from @chaos/protocol
    trimHex: family.trimHex, // LOCKED
    tell: family.tell, // LOCKED
    face: family.face, // LOCKED
    headBlock,
    torsoSize,
    armStyle,
    legStyle,
    accessory,
    accentHex,
    pattern,
    eyeStyle,
    mouth,
    phaseOffset,
    scatterSeed,
  };
}

/** Honest combination count per family, for the doc and the panel readout.
 *  Face axes only count where the family actually shows a face. */
export function familyCombinations(familyId) {
  const f = FAMILIES[familyId];
  if (!f) return 0;
  const faceCombos = f.face === 'eyes' ? EYE_STYLES.length * MOUTHS.length : 1;
  return (
    f.heads.length *
    f.torsos.length *
    ARM_STYLES.length *
    LEG_STYLES.length *
    f.accessories.length *
    ACCENT_SWATCHES.length *
    PATTERNS.length *
    faceCombos
  );
}
